import React, { useState } from "react";
import CustomDropdown from "../../common/CustomDropdown";
import ViewModeToggle from "../../common/ViewModeToggle";

const MobileTenderTabs = ({
  activeTab,
  onTabChange,
  onFilterClick,
  sortBy,
  handleSortChange,
  searchTerm,
  handleSearchChange,
  onSubTabChange,
  activeSubTab,
  activePositionFilter,
  handlePositionFilterChange,
  // Same name and same meaning as the desktop TenderTabs prop: drop the
  // TP Requests / TP Lite Requests pair. The category dropdown on that row is
  // NOT part of the switch and stays, so "My Requests" keeps its
  // Players / Coaches / Staffs / Others picker on a phone.
  hideTenderTabs = false,
  // Optional node rendered directly UNDER the source-tabs row (Marin's row
  // order: the lifecycle pills are a filter INSIDE "My Requests" and may not
  // sit above the source choice). The club requests page slots its lifecycle
  // segment in here on the phone; left out -> nothing changes.
  lifecycleRow = null,
  // Cards / List. The phone had no way to reach the switch at all, so anyone
  // who once landed in list mode (?view_mode=list, or the mode remembered in
  // localStorage by useViewMode) was stuck there. Same prop names and the same
  // `onViewModeChange &&` gate the desktop row (club/TenderTabs.js:114) and the
  // agent's own MOBILE row (agent/responsive/AgentTendersMobileTabs.js:252)
  // already use; left out -> the row renders exactly as before.
  viewMode,
  onViewModeChange,
}) => {
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  const handleSearchIconClick = () => {
    setIsSearchVisible(true);
  };

  const handleCloseSearch = () => {
    setIsSearchVisible(false);
  };

  if (isSearchVisible) {
    // The open search replaces the tab rows, as it always has — the lifecycle
    // pills stay visible below it, exactly as they stayed visible when they
    // still lived above this component.
    return (
      <>
      <div className="px-4 py-3 bg-white flex items-center gap-2 border-y border-[#0000001A]">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.5 9.5L7.325 7.325M8.5 4.5C8.5 6.70914 6.70914 8.5 4.5 8.5C2.29086 8.5 0.5 6.70914 0.5 4.5C0.5 2.29086 2.29086 0.5 4.5 0.5C6.70914 0.5 8.5 2.29086 8.5 4.5Z"
                stroke="#1A212F"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search by position or club name"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-8 pr-4 py-2 border border-[#E6E6E6] placeholder:text-sm placeholder:text-[#1E1E1E] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            autoFocus
          />
        </div>
        <button
          onClick={handleCloseSearch}
          className="p-2"
          aria-label="Close search"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13M1 1L13 13"
              stroke="#1A212F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      {lifecycleRow}
      </>
    );
  }

  return (
    <>
      {/* The phone header keeps ONE JOB PER ROW (Marin's layout ruling): the
          source tabs get the full width on their own row and scroll in the
          same style as the lifecycle pill row above — no label ever wraps,
          truncates or slides out of reach behind an icon cluster. The
          tools (loupe, filter, Pro) move to their own right-aligned row
          below; the category dropdown row stays as it is. */}
      <div className="px-4 bg-white border-t border-[#0000001A]">
        {/* The inner mx-auto centres the tabs while they fit (Marin's polish)
            and collapses to zero the moment they overflow, so the strip keeps
            scrolling from its start — justify-center on a scroll container
            would clip the first tab instead. Centred on PHONES ONLY: from the
            tablet breakpoint up (md:, and this component still renders at
            exactly 768px) the tabs sit left like the lifecycle pills above
            and the desktop row. */}
        <div className="flex overflow-x-auto [scrollbar-width:none]">
          <div className="flex items-center gap-4 mx-auto md:mx-0">
          <div
            onClick={() => onTabChange("my-tenders")}
            className={`py-4 text-xs whitespace-nowrap flex-none ${
              activeTab === "my-tenders"
                ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                : "text-[#1E1E1ECC]"
            }`}
          >
            My Requests
          </div>
          <div
            onClick={() => onTabChange("all-listed-tenders")}
            className={`py-4 text-xs whitespace-nowrap flex-none ${
              activeTab === "all-listed-tenders"
                ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                : "text-[#1E1E1ECC]"
            }`}
          >
            All listed Requests
          </div>
          <div
            onClick={() => onTabChange("saved")}
            className={`py-4 text-xs whitespace-nowrap flex-none ${
              activeTab === "saved"
                ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                : "text-[#1E1E1ECC]"
            }`}
          >
            Saved
          </div>
          </div>
        </div>
      </div>

      {/* Lifecycle pills — under the source tabs, only while My Requests is
          the source (the caller passes null otherwise). */}
      {lifecycleRow}

      {/* Tools row: loupe, filter, Pro — right-aligned, own row. */}
      <div className="px-4 py-2 bg-white flex justify-end items-center border-y border-[#0000001A]">
        <div className="flex items-center gap-2 flex-none">
          <div
            className="p-3 bg-[#F7F7F7] rounded-lg"
            onClick={handleSearchIconClick}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.5 9.5L7.325 7.325M8.5 4.5C8.5 6.70914 6.70914 8.5 4.5 8.5C2.29086 8.5 0.5 6.70914 0.5 4.5C0.5 2.29086 2.29086 0.5 4.5 0.5C6.70914 0.5 8.5 2.29086 8.5 4.5Z"
                stroke="#1A212F"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="p-3 bg-[#F7F7F7] rounded-lg" onClick={onFilterClick}>
            <svg
              width="12"
              height="10"
              viewBox="0 0 12 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11 0.5H1L5 5.23V8.5L7 9.5V5.23L11 0.5Z"
                stroke="#1A212F"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="text-xs">
            <CustomDropdown
              options={[
                { value: "pro", label: "Pro" },
                { value: "youth", label: "Youth" },
              ]}
              value={sortBy}
              onChange={handleSortChange}
              placeholder="Pro"
              className="flex items-center gap-2 p-3 bg-[#E9F3FF] text-[#2283FF] rounded-lg font-medium"
            />
          </div>
        </div>
      </div>

      {/* Category row. It now carries the Cards/List toggle at its right end,
          the same place the desktop keeps it (club/TenderTabs.js:114-117), so
          the switch sits next to the category choice on every width instead of
          in a row of its own.
          On 375 this row does not always fit: with the TP / TP Lite pair on
          "All listed Requests" it needs roughly 476px. It therefore SCROLLS
          sideways rather than squashing — the pattern the source-tab row above
          uses (:113) and the agent's mobile controls row uses
          (agent/responsive/AgentTendersMobileTabs.js:249). `flex-none` on both
          clusters is what makes them keep their size instead of being
          compressed; nothing is shrunk, truncated or dropped. */}
      <div
        className={`px-4 py-2 bg-white flex ${
          hideTenderTabs ? "justify-end" : "justify-between"
        } items-center gap-3 overflow-x-auto [scrollbar-width:none] border-b border-[#0000001A]`}
      >
        {!hideTenderTabs && (
          <div className="flex items-center gap-4 flex-none">
            <div
              onClick={() => onSubTabChange("tp-tenders")}
              className={`py-4  text-xs ${
                activeSubTab === "tp-tenders"
                  ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                  : "text-[#1E1E1ECC]"
              }`}
            >
              TP Requests
            </div>
            <div
              onClick={() => onSubTabChange("tp-lite-tenders")}
              className={`py-4  text-xs ${
                activeSubTab === "tp-lite-tenders"
                  ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                  : "text-[#1E1E1ECC]"
              }`}
            >
              TP Lite Requests
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 flex-none">
          <div className="text-xs">
            <CustomDropdown
              options={[
                { value: "players", label: "Players" },
                { value: "coaches", label: "Coaches" },
                { value: "staffs", label: "Staffs" },
                { value: "others", label: "Others" },
              ]}
              value={activePositionFilter}
              onChange={handlePositionFilterChange}
              placeholder="Players"
              className="flex items-center gap-2 p-3 bg-[#E9F3FF] text-[#2283FF] rounded-lg "
            />
          </div>
          {onViewModeChange && (
            <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
          )}
        </div>
      </div>
    </>
  );
};

export default MobileTenderTabs;
